import type { DXBlueprintContent } from "../../../services/contentService";
import type { ContentDataType } from "../ContentManagement";

/**
 * Transform API content data to component data format
 */
export const transformApiContentToTableData = (apiContent: DXBlueprintContent): ContentDataType => {
  // Map API status to component status
  const statusMap = {
    'draft': 'progress',
    'published': 'published',
    'archived': 'archived',
    'deleted': 'deleted'
  } as const;

  return {
    key: apiContent.dx_number,
    sk: apiContent.SK,
    dxNumber: apiContent.dx_number,
    title: apiContent.title_th || apiContent.title_en || 'Untitled',
    creator: apiContent.author_name || 'Unknown',
    createdAt: apiContent.created_at,
    updatedAt: apiContent.updated_at,
    updatedBy: apiContent.author_name || 'Unknown', // API doesn't have updatedBy, using author_name as fallback
    version: String(apiContent.version || 1),
    status: statusMap[apiContent.status as keyof typeof statusMap] || 'draft',
    edit_status: apiContent.edit_status || 'draft',
    content_owner: apiContent.content_owner || '',
    content_members: apiContent.content_members || []
  };
};

/**
 * Transform multiple API contents to table data format
 */
export const transformApiContentsToTableData = (apiContents: DXBlueprintContent[]): ContentDataType[] => {
  return apiContents.map(transformApiContentToTableData);
};

/**
 * Get API status from component status
 */
export const getApiStatusFromComponentStatus = (componentStatus: string): DXBlueprintContent['status'] => {
  const statusMap = {
    'progress': 'draft',
    'published': 'published',
    'archived': 'archived',
    'deleted': 'deleted'
  } as const;

  return statusMap[componentStatus as keyof typeof statusMap] || 'draft';
};

/**
 * Build API parameters from component filters
 */
export const buildApiParams = (
  filter?: string,
  searchText?: string,
  page: number = 1,
  limit: number = 20
) => {
  const params: Record<string, any> = {
    page,
    limit,
    sort_by: 'updated_at',
    order: 'desc'
  };

  if (filter) {
    params.status = getApiStatusFromComponentStatus(filter);
  }

  if (searchText && searchText.trim()) {
    // For simple search, we can use the search endpoint
    // For now, we'll just include it as a parameter
    params.search = searchText.trim();
  }

  return params;
};